import React, { useState, useEffect } from 'react'
import ExtendWebsiteWidget from './ExtendWebsiteWidget';
import { Container, Skeleton } from "@bsf/force-ui";
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import ErrorBoundary from './ErrorBoundary';

// Simple component to handle the actual data fetching and rendering
const ExtendWebsiteContent = () => {
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updateCounter, setUpdateCounter] = useState(0);
    const [allInstalled, setAllInstalled] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        
        const fetchSettings = () => {
            if (!isMounted) return;
        
            setLoading(true);
            setError(null);
        
            apiFetch({
                path: '/uavc/v1/plugins',
                headers: {
                    'Content-Type': 'application/json',
                    ...(window.uavcSettingsData && window.uavcSettingsData.uavc_nonce_action ? 
                        { 'X-WP-Nonce': window.uavcSettingsData.uavc_nonce_action } : {})
                },
            })
            .then(data => {
                if (!isMounted) return;
        
                if (!data) {
                    throw new Error('No data received from API');
                }
        
                const pluginsArray = [];
        
                Object.keys(data).forEach(key => {
                    const pluginData = data[key] || {};
                    pluginsArray.push({
                        path: key,
                        slug: pluginData.slug || key.split('/').pop().replace('.php', ''),
                        name: Array.isArray(pluginData.name) ? pluginData.name[0] : (pluginData.name || key),
                        icon: Array.isArray(pluginData.icon) ? pluginData.icon[0] : (pluginData.icon || ''),
                        type: pluginData.type || 'plugin',
                        desc: pluginData.desc || '',
                        status: pluginData.status || 'Install',
                        is_installed: pluginData.is_installed || false,
                        siteUrl: pluginData.siteUrl || pluginData.siteurl || '#',
                        settings_url: pluginData.settings_url || '#',
                        isFree: pluginData.isFree !== undefined ? pluginData.isFree : true,
                    });
                });
        
                if (!isMounted) return;
        
                setPlugins(pluginsArray);
        
                const areAllInstalled = pluginsArray.length > 0 &&
                    pluginsArray.every(plugin => plugin.is_installed);
                setAllInstalled(areAllInstalled);
            })
            .catch(err => {
                if (!isMounted) return;
                console.error("Error fetching plugins:", err);
                setError(err.message);
            })
            .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        };
        
        fetchSettings();
        
        return () => {
            isMounted = false;
        };
    }, [updateCounter]);

    // If all plugins are installed, don't render the component
    if (allInstalled) {
        return null;
    }
    
    // Show error state
    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600">{__('Error loading plugins', 'ultimate_vc')}: {error}</p>
                <button 
                    className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                    onClick={() => setUpdateCounter(prev => prev + 1)}
                >
                    {__('Retry', 'ultimate_vc')}
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col rounded-lg p-4" style={{ backgroundColor: "white" }}>
            {loading ? (
                <Container
                    align="stretch"
                    className="gap-1 p-1 grid grid-cols-1 md:grid-cols-2"
                    containerType="grid"
                    justify="start"
                >
                    {[...Array(2)].map((_, index) => (
                        <Container.Item
                            key={index}
                            alignSelf="auto"
                            style={{ height: '150px' }}
                            className="text-wrap rounded-md shadow-container-item bg-[#F9FAFB] p-4"
                        >
                            <div className="flex flex-col gap-6" style={{ marginTop: '40px' }}>
                                <Skeleton className="w-12 h-2 rounded-md" />
                                <Skeleton className="w-16 h-2 rounded-md" />
                                <Skeleton className="w-12 h-2 rounded-md" />
                            </div>
                        </Container.Item>
                    ))}
                </Container>
            ) : plugins.length > 0 ? (
                <Container
                    align="stretch"
                    className="gap-1 p-1 grid grid-cols-1 md:grid-cols-2"
                    containerType="grid"
                    justify="start"
                    style={{ backgroundColor: "#F9FAFB" }}
                >
                    {plugins.slice(0, 4).map((plugin, index) => (
                        <Container.Item
                            key={`plugin-${index}-${plugin.slug || 'unknown'}`}
                            alignSelf="auto"
                            className="text-wrap rounded-md shadow-container-item bg-background-primary p-4"
                        >
                            <ExtendWebsiteWidget 
                                plugin={plugin} 
                                setUpdateCounter={setUpdateCounter} 
                            />
                        </Container.Item>
                    ))}
                </Container>
            ) : (
                <div className="text-center p-4">
                    {__("No plugins available", "ultimate_vc")}
                </div>
            )}
        </div>
    );
};

// Main component wrapped with error boundary
const ExtendWebsite = () => {
    return (
        <div className="rounded-lg bg-white w-full">
            <div className="flex items-center justify-between p-4" style={{ paddingBottom: '0' }}>
                <p className="m-0 text-sm font-semibold text-text-primary">
                    {__("Extend Your Website", "ultimate_vc")}
                </p>
                <div className="flex items-center gap-x-2 mr-7"></div>
            </div>
            
            <ErrorBoundary>
                <ExtendWebsiteContent />
            </ErrorBoundary>
        </div>
    );
};

export default ExtendWebsite;
